import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { CancelOutlined } from '@mui/icons-material';
import { formatCurrency } from './accountOverview.utils';

export default function AccountOverviewCancelProductDialog({
  open,
  product,
  details,
  billingExempt,
  cancelling,
  onClose,
  onConfirm,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          border: '1px solid rgba(15, 23, 42, 0.08)',
          boxShadow: '0 28px 70px -30px rgba(15, 23, 42, 0.55)',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(220, 38, 38, 0.08)',
              color: '#b91c1c',
              border: '1px solid rgba(220, 38, 38, 0.18)',
            }}
          >
            <CancelOutlined sx={{ fontSize: 21 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
              Cancelar {product?.name}
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', mt: 0.35 }}>
              O acesso continua ativo até o fim do período já pago.
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={1.5}>
          <Box
            sx={{
              p: 1.75,
              borderRadius: '14px',
              bgcolor: 'rgba(248, 250, 252, 0.95)',
              border: '1px solid rgba(15, 23, 42, 0.08)',
            }}
          >
            <Typography variant="body2" sx={{ color: '#334155', lineHeight: 1.55 }}>
              Vamos agendar o cancelamento de <strong>{product?.name}</strong>.
              O produto permanece disponível agora e será desativado automaticamente na data final do ciclo.
            </Typography>
          </Box>

          {billingExempt && (
            <Box
              sx={{
                p: 1.75,
                borderRadius: '14px',
                bgcolor: 'rgba(8, 145, 178, 0.08)',
                border: '1px solid rgba(8, 145, 178, 0.18)',
              }}
            >
              <Typography variant="body2" sx={{ color: '#155e75', lineHeight: 1.55, fontWeight: 600 }}>
                Esta conta está com acesso liberado, então o cancelamento não passa pela Stripe.
                O acesso será mantido até a próxima data mensal do contrato.
              </Typography>
            </Box>
          )}

          {details?.hasRemainingProducts ? (
            <Box
              sx={{
                p: 1.75,
                borderRadius: '14px',
                bgcolor: 'rgba(16, 185, 129, 0.08)',
                border: '1px solid rgba(16, 185, 129, 0.18)',
              }}
            >
              <Stack spacing={0.9}>
                <Typography variant="body2" sx={{ color: '#065f46', fontWeight: 700 }}>
                  Os outros produtos continuam ativos.
                </Typography>
                {details.hasCurrentMonthlyAmount ? (
                  <>
                    <Stack direction="row" justifyContent="space-between" gap={2}>
                      <Typography variant="body2" sx={{ color: '#475569' }}>
                        Mensalidade atual
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 800 }}>
                        {formatCurrency(details.currentMonthlyAmount, 'brl')}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" gap={2}>
                      <Typography variant="body2" sx={{ color: '#475569' }}>
                        Nova mensalidade estimada
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 800 }}>
                        {formatCurrency(details.nextMonthlyAmount, 'brl')}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" gap={2}>
                      <Typography variant="body2" sx={{ color: '#475569' }}>
                        Redução mensal
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#047857', fontWeight: 800 }}>
                        {formatCurrency(details.monthlyReduction, 'brl')}
                      </Typography>
                    </Stack>
                  </>
                ) : (
                  <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.55 }}>
                    A mensalidade dos produtos restantes será recalculada pela regra de desconto progressivo.
                  </Typography>
                )}
              </Stack>
            </Box>
          ) : (
            <Box
              sx={{
                p: 1.75,
                borderRadius: '14px',
                bgcolor: 'rgba(245, 158, 11, 0.10)',
                border: '1px solid rgba(245, 158, 11, 0.22)',
              }}
            >
              <Typography variant="body2" sx={{ color: '#92400e', lineHeight: 1.55, fontWeight: 600 }}>
                Este é o último produto ativo. Ao confirmar, o acesso será mantido até o fim do período pago
                e a assinatura será encerrada depois disso.
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          onClick={onClose}
          disabled={cancelling}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 700,
            color: '#475569',
            '&:hover': { background: 'rgba(15, 23, 42, 0.04)' },
          }}
        >
          Voltar
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          disabled={cancelling}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 800,
            boxShadow: '0 12px 24px -12px rgba(220, 38, 38, 0.75)',
          }}
        >
          {cancelling ? 'Agendando...' : 'Confirmar cancelamento'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
